<template>
  <div class="show_all_content_wrapper">
    <main>
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>

        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-select-input
                col="6"
                :optionsList="purposeOptions"
                :placeholder="$t('payouts.purpose')"
                v-model="filterOptions.purpose"
              />
              <base-select-input
                col="6"
                :optionsList="statusOptions"
                :placeholder="$t('payouts.fields.status')"
                v-model="filterOptions.status"
              />
              <base-input
                col="6"
                type="number"
                :placeholder="$t('payouts.fields.booking_id')"
                v-model.trim="filterOptions.booking_id"
              />
              <base-input
                col="6"
                type="number"
                :placeholder="$t('payouts.fields.user_id')"
                v-model.trim="filterOptions.user_id"
              />
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>

      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("SIDENAV.payouts.title") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>
      </div>

      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>

        <template v-slot:[`item.purpose`]="{ item }">
          {{ purposeLabel(item.purpose) }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <span :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span>
        </template>
        <template v-slot:[`item.part`]="{ item }">
          {{ partLabel(item.part) }}
        </template>
        <template v-slot:[`item.user`]="{ item }">
          {{ item.user && item.user.name ? item.user.name : item.user_id || "-" }}
        </template>
        <template v-slot:[`item.booking_number`]="{ item }">
          {{ item.booking_number || item.booking_id || "-" }}
        </template>
        <template v-slot:[`item.currency`]="{ item }">
          {{ currencyLabel(item.currency) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <a-tooltip placement="bottom" v-if="$can('payouts show', 'payouts')">
              <template slot="title">
                <span>{{ $t("BUTTONS.show") }}</span>
              </template>
              <button class="btn_show" @click="showItem(item)">
                <i class="fal fa-eye"></i>
              </button>
            </a-tooltip>

            <a-tooltip
              placement="bottom"
              v-if="$can('payouts cancel', 'payouts') && canCancel(item)"
            >
              <template slot="title">
                <span>{{ $t("BUTTONS.cancel") }}</span>
              </template>
              <button class="btn_delete" @click="selectCancelItem(item)">
                <i class="fal fa-ban"></i>
              </button>
            </a-tooltip>
          </div>
        </template>

        <template v-slot:top>
          <v-dialog v-model="dialogCancel" max-width="500">
            <v-card>
              <v-card-title class="text-h5 justify-center">
                {{ $t("payouts.cancelConfirm") }}
              </v-card-title>
              <v-card-actions>
                <v-btn
                  class="modal_confirm_btn"
                  :loading="isCancelling"
                  :disabled="isCancelling"
                  @click="confirmCancelItem"
                >
                  {{ $t("BUTTONS.ok") }}
                </v-btn>
                <v-btn
                  class="modal_cancel_btn"
                  :disabled="isCancelling"
                  @click="dialogCancel = false"
                >
                  {{ $t("BUTTONS.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
      </v-data-table>
    </main>

    <div class="pagination_container text-center mt-3 mb-0">
      <v-pagination
        class="py-0"
        square
        v-model="paginations.current_page"
        :length="paginations.last_page"
        :total-visible="6"
        @input="updateRouterQueryParam($event)"
        :prev-icon="
          getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
        "
        :next-icon="
          getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
        "
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import payoutLabels from "@/utils/payoutLabels";

export default {
  name: "AllPayouts",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    purposeOptions() {
      return [
        { id: null, name: this.$t("payouts.allPurposes") },
        { id: "booking_release", name: this.purposeLabel("booking_release") },
        { id: "cancellation_refund", name: this.purposeLabel("cancellation_refund") },
        { id: "wallet_settlement", name: this.purposeLabel("wallet_settlement") },
      ];
    },

    statusOptions() {
      return [
        { id: null, name: this.$t("payouts.allStatuses") },
        ...payoutLabels.rowStatuses.map((status) => ({
          id: status,
          name: this.statusLabel(status),
        })),
      ];
    },

    tableHeaders() {
      return [
        { text: this.$t("payouts.fields.id"), value: "id", sortable: false, align: "center" },
        { text: this.$t("payouts.fields.purpose"), value: "purpose", sortable: false, align: "center" },
        { text: this.$t("payouts.fields.status"), value: "status", sortable: false, align: "center" },
        { text: this.$t("payouts.fields.part"), value: "part", sortable: false, align: "center" },
        { text: this.$t("payouts.fields.booking_number"), value: "booking_number", sortable: false, align: "center" },
        { text: this.$t("payouts.fields.user"), value: "user", sortable: false, align: "center" },
        { text: this.$t("payouts.fields.amount"), value: "amount", sortable: false, align: "center" },
        { text: this.$t("payouts.fields.transferred_amount"), value: "transferred_amount", sortable: false, align: "center" },
        { text: this.$t("payouts.fields.currency"), value: "currency", sortable: false, align: "center" },
        { text: this.$t("payouts.fields.payoutId"), value: "payoutId", sortable: false, align: "center" },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ];
    },
  },

  data() {
    return {
      loading: false,
      isWaitingRequest: false,
      isCancelling: false,
      filterFormIsActive: false,
      filterOptions: {
        purpose: null,
        status: null,
        booking_id: null,
        user_id: null,
      },
      tableRows: [],
      dialogCancel: false,
      itemToCancel: null,
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 10,
      },
    };
  },

  watch: {
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
  },

  methods: {
    purposeLabel(value) {
      return payoutLabels.purpose(this, value);
    },
    statusLabel(value) {
      return payoutLabels.status(this, value);
    },
    partLabel(value) {
      return payoutLabels.part(this, value);
    },
    currencyLabel(value) {
      return payoutLabels.currency(this, value);
    },
    statusClass(status) {
      return payoutLabels.statusClass(status);
    },
    canCancel(row) {
      return payoutLabels.canCancel(row);
    },

    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/payouts/all", query: { page: 1 } });
      }
      this.setTableRows();
    },

    async resetFilter() {
      this.filterOptions = {
        purpose: null,
        status: null,
        booking_id: null,
        user_id: null,
      };
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/payouts/all", query: { page: 1 } });
      }
      this.setTableRows();
    },

    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    },

    async setTableRows() {
      this.loading = true;
      this.isWaitingRequest = true;
      try {
        const res = await this.$axios({
          method: "GET",
          url: "payouts",
          params: {
            page: this.paginations.current_page,
            limit: this.paginations.items_per_page,
            purpose: this.filterOptions.purpose?.id || undefined,
            status: this.filterOptions.status?.id || undefined,
            booking_id: this.filterOptions.booking_id || undefined,
            user_id: this.filterOptions.user_id || undefined,
          },
        });
        const payload = res.data.data;
        this.tableRows = Array.isArray(payload) ? payload : payload?.data || [];
        const meta = payload?.meta || res.data.meta || {};
        this.paginations.last_page = meta.last_page || 1;
        this.paginations.items_per_page = meta.per_page || 10;
      } catch (error) {
        console.log(error.response?.data?.message);
      } finally {
        this.loading = false;
        this.isWaitingRequest = false;
      }
    },

    showItem(item) {
      this.$router.push({ path: `/payouts/show/${item.id}` });
    },

    selectCancelItem(item) {
      this.dialogCancel = true;
      this.itemToCancel = item;
    },

    async confirmCancelItem() {
      if (!this.itemToCancel?.id) return;
      this.isCancelling = true;
      try {
        await this.$axios({
          method: "POST",
          url: `payouts/${this.itemToCancel.id}/cancel`,
        });
        this.dialogCancel = false;
        this.itemToCancel = null;
        this.$message.success(this.$t("payouts.cancelSuccess"));
        this.setTableRows();
      } catch (error) {
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      } finally {
        this.isCancelling = false;
      }
    },
  },

  created() {
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
  },
};
</script>
